/**
 * ReturnRefundTimeline — customer-facing vertical timeline that surfaces
 * return / refund milestones in chronological order.  The component is
 * intentionally minimal: it reads `order.return_history` (an append-only
 * array maintained by the admin endpoint + Razorpay webhook) and falls
 * back to the timestamped `*_at` fields when no history exists.
 *
 * Renders NOTHING when there is no return / refund activity on the order
 * so existing order screens remain unchanged for normal orders.
 */
import { Check, RefreshCw, AlertCircle, IndianRupee, Hash } from "lucide-react";

const LABELS = {
  delivered:         "Delivered",
  return_requested:  "Return Requested",
  return_approved:   "Return Approved",
  return_rejected:   "Return Rejected",
  refund_initiated:  "Refund Initiated",
  refund_completed:  "Refund Completed",
  refund_failed:     "Refund Failed",
  refund_pending:    "Refund Pending",
};

const isReturnFlowStatus = (s) =>
  s === "return_requested" || s === "return_approved" || s === "return_rejected" ||
  s === "refund_initiated" || s === "refund_completed";

export default function ReturnRefundTimeline({ order, testId = "return-refund-timeline" }) {
  if (!order) return null;
  const history = Array.isArray(order.return_history) ? order.return_history : [];
  // Hide entirely unless the order has return-flow activity OR is currently in a return/refund status.
  if (history.length === 0 && !isReturnFlowStatus(order.order_status)) return null;

  // Build a synthetic list when history is empty but order is in a return status (defensive).
  const items = history.length > 0 ? history : [{
    status: order.order_status,
    label: LABELS[order.order_status] || order.order_status,
    at: order[`${order.order_status}_at`] || order.created_at,
  }];

  // Prepend delivered milestone for context, if present and not already in history.
  const hasDelivered = items.some((h) => h.status === "delivered");
  if (!hasDelivered && order.delivered_at) {
    items.unshift({ status: "delivered", label: "Delivered", at: order.delivered_at });
  }

  return (
    <div className="bg-white border border-brand-line rounded-2xl p-5 sm:p-6" data-testid={testId}>
      <h3 className="font-serif text-xl text-brand-dark mb-1 flex items-center gap-2">
        <RefreshCw className="w-4 h-4 text-brand-chili" strokeWidth={1.6} />
        Return &amp; Refund Updates
      </h3>
      <p className="text-xs text-brand-muted mb-4">
        Latest status of your return / refund request. Reach us on WhatsApp for any questions.
      </p>
      <ol className="relative border-l-2 border-brand-line ml-2 space-y-5">
        {items.map((h, i) => {
          const isFail = h.status === "refund_failed";
          const Icon = isFail ? AlertCircle : Check;
          return (
            <li key={`${h.status}-${h.at || i}`} className="pl-5 relative" data-testid={`${testId}-item-${i}`}>
              <span className={`absolute -left-[11px] top-0.5 w-5 h-5 rounded-full flex items-center justify-center ${isFail ? "bg-red-100 text-red-700" : "bg-brand-chili text-white"}`}>
                <Icon className="w-3 h-3" strokeWidth={2.2} />
              </span>
              <div className="flex justify-between items-baseline gap-3">
                <div className="font-medium text-brand-dark">{h.label || LABELS[h.status] || h.status}</div>
                <div className="text-[11px] text-brand-muted whitespace-nowrap">
                  {h.at ? new Date(h.at).toLocaleString() : ""}
                </div>
              </div>
              {h.return_reason && (
                <div className="text-xs text-brand-muted mt-1">Reason: {h.return_reason}</div>
              )}
              {h.refund_amount != null && (
                <div className="text-xs text-brand-muted mt-1 flex items-center gap-1">
                  <IndianRupee className="w-3 h-3" strokeWidth={1.7} />
                  Refund amount: ₹{Number(h.refund_amount).toFixed(0)}
                </div>
              )}
              {(h.refund_ref_id || h.razorpay_refund_id) && (
                <div className="text-xs text-brand-muted mt-1 flex items-center gap-1">
                  <Hash className="w-3 h-3" strokeWidth={1.7} />
                  Refund reference: <span className="font-mono">{h.razorpay_refund_id || h.refund_ref_id}</span>
                </div>
              )}
              {h.error && (
                <div className="text-xs text-red-700 mt-1">{h.error}</div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
